import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Layout as AntLayout, Drawer } from 'antd';
import { Header } from './Header';
import { Footer } from './Footer';
import { Sidebar } from './Sidebar';
import { useLocation } from 'react-router-dom';

const { Content, Sider } = AntLayout;

const DEFAULT_SIDEBAR_WIDTH = 260;
const SIDEBAR_MIN_WIDTH = 220;
const SIDEBAR_MAX_WIDTH = 420;

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const siderRef = useRef<HTMLDivElement | null>(null);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);

  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_SIDEBAR_WIDTH;
    }
    const stored = window.localStorage.getItem('layout.sidebarWidth');
    const parsed = stored ? Number.parseInt(stored, 10) : NaN;
    if (Number.isFinite(parsed)) {
      return Math.min(Math.max(parsed, SIDEBAR_MIN_WIDTH), SIDEBAR_MAX_WIDTH);
    }
    return DEFAULT_SIDEBAR_WIDTH;
  });

  // Rotas com layout mínimo (sem header ou sidebar)
  const minimalRoutes = ['/login', '/registrar', '/esqueci-minha-senha', '/reset-password'];
  const isMinimalLayout = minimalRoutes.includes(location.pathname);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('layout.sidebarWidth', String(Math.round(sidebarWidth)));
  }, [sidebarWidth]);

  useEffect(() => {
    if (!isResizingSidebar) {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      return;
    }

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const handleMouseMove = (event: MouseEvent) => {
      if (!siderRef.current) return;
      const rect = siderRef.current.getBoundingClientRect();
      const proposedWidth = event.clientX - rect.left;
      const clampedWidth = Math.min(Math.max(proposedWidth, SIDEBAR_MIN_WIDTH), SIDEBAR_MAX_WIDTH);
      setSidebarWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizingSidebar(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizingSidebar]);

  const handleResizeStart = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
    }
    setIsResizingSidebar(true);
  }, [sidebarCollapsed]);

  if (isMinimalLayout) {
    return (
      <AntLayout style={{ height: '100vh', overflow: 'hidden' }}>
        <Content style={{ overflowY: 'auto', overflowX: 'hidden' }}>
          <main id="conteudo-principal" role="main" style={{ minHeight: '100%' }}>
            {children}
          </main>
        </Content>
      </AntLayout>
    );
  }

  return (
    <AntLayout style={{ height: '100vh', overflow: 'hidden' }}>
      {/* Desktop Sidebar */}
      <Sider
        ref={siderRef}
        collapsible
        collapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
        trigger={null}
        breakpoint="lg"
        // Mantém ícones quando recolhido
        collapsedWidth={64}
        width={sidebarCollapsed ? 64 : sidebarWidth}
        onBreakpoint={(broken) => {
          if (broken) {
            setMobileMenuVisible(true);
          } else {
            setMobileMenuVisible(false);
          }
        }}
        style={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: sidebarCollapsed ? 64 : sidebarWidth,
          minWidth: sidebarCollapsed ? 64 : SIDEBAR_MIN_WIDTH,
          maxWidth: sidebarCollapsed ? 64 : SIDEBAR_MAX_WIDTH,
          transition: isResizingSidebar ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        theme="light"
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        {!sidebarCollapsed && (
          <div
            onMouseDown={handleResizeStart}
            role="presentation"
            style={{
              position: 'absolute',
              top: 0,
              right: -3,
              width: 6,
              height: '100%',
              cursor: 'col-resize',
              zIndex: 5,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 2,
                height: 48,
                background: '#d9d9d9',
                borderRadius: 2,
              }}
            />
          </div>
        )}
      </Sider>

      {/* Mobile Sidebar Drawer */}
      <Drawer
        title="Menu"
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={280}
        styles={{ body: { padding: 0 } }}
      >
        <Sidebar mobile onNavigate={() => setMobileMenuVisible(false)} />
      </Drawer>

      {/* Header */}
      <AntLayout style={{ height: '100vh', overflow: 'hidden' }}>
        <Header
          onMenuClick={() => setMobileMenuVisible(true)}
          showMenuButton={mobileMenuVisible}
        />
        <Content style={{ overflowY: 'auto', overflowX: 'hidden' }}>
          <main id="conteudo-principal" role="main" style={{ minHeight: '100%' }}>
            {children}
          </main>
        </Content>
        <Footer />
      </AntLayout>
    </AntLayout>
  );
}
